import { AxiosResponse } from "axios";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import {
  lookupDataSliceAction,
  LookupCategoryType,
  LookupDataItemType,
} from "../../slices/lookup-data";
import {
  createLookupDataSagaAction,
  deleteLookupDataSagaAction,
  getLookupByCategorySagaAction,
  lookupMappingsSagaAction,
  lookupDataSagaActionTypes,
} from "./saga-actions";

type GetLookupDataApiResponseType = {
  success: boolean;
  message: string;
  data: LookupDataItemType[];
};

type CreateLookupDataApiResponseType = {
  success: boolean;
  message: string;
};

type DeleteLookupDataApiResponseType = {
  success: boolean;
  message: string;
};

type LookupMappingsApiResponseType = {
  success: boolean;
  message: string;
};

const getLookupByCategoryAPI = async (category: LookupCategoryType) => {
  const response = await axiosRestInstance
    .get<AxiosResponse<GetLookupDataApiResponseType>>(
      `/lookupData/getlookupData/${category}`
    )
    .then((res) => res.data);
  return response;
};

const createLookupDataAPI = async (payload: {
  category: LookupCategoryType;
  value: string;
}) => {
  const response = await axiosRestInstance
    .post<AxiosResponse<CreateLookupDataApiResponseType>>(
      "/lookupData/createLookupData",
      payload
    )
    .then((res) => res.data);
  return response;
};

const deleteLookupDataAPI = async (ID: number) => {
  const response = await axiosRestInstance
    .delete<AxiosResponse<DeleteLookupDataApiResponseType>>(
      `/lookupData/delete/${ID}`
    )
    .then((res) => res.data);
  return response;
};

const lookupMappingsAPI = async (payload: {
  category: LookupCategoryType;
  module: string;
  ids: number[];
}) => {
  const response = await axiosRestInstance
    .put<AxiosResponse<LookupMappingsApiResponseType>>(
      "/lookupData/lookupMappings",
      payload
    )
    .then((res) => res.data);
  return response;
};

function* getLookupByCategorySaga(
  action: ReturnType<typeof getLookupByCategorySagaAction>
) {
  try {
    yield put(
      lookupDataSliceAction.setLookupCategoryLoading({
        category: action.payload.category,
        loading: true,
      })
    );
    const response: GetLookupDataApiResponseType = yield call(
      getLookupByCategoryAPI,
      action.payload.category
    );
    yield put(
      lookupDataSliceAction.setLookupCategoryData({
        category: action.payload.category,
        data: response?.data || [],
      })
    );
    action.payload.onSuccess?.();
  } catch (error: any) {
    const message =
      error?.response?.data?.message || "Unable to fetch lookup data.";
    yield put(lookupDataSliceAction.setLookupErrorMessage(message));
    action.payload.onError?.(message);
  } finally {
    yield put(
      lookupDataSliceAction.setLookupCategoryLoading({
        category: action.payload.category,
        loading: false,
      })
    );
  }
}

function* createLookupDataSaga(
  action: ReturnType<typeof createLookupDataSagaAction>
) {
  try {
    yield put(lookupDataSliceAction.setLookupCreateLoading(true));
    const response: CreateLookupDataApiResponseType = yield call(
      createLookupDataAPI,
      {
        category: action.payload.category,
        value: action.payload.value,
      }
    );
    action.payload.onSuccess?.(response?.message || "Data created successfully.");
  } catch (error: any) {
    const message =
      error?.response?.data?.message || "Unable to create lookup data.";
    yield put(lookupDataSliceAction.setLookupErrorMessage(message));
    action.payload.onError?.(message);
  } finally {
    yield put(lookupDataSliceAction.setLookupCreateLoading(false));
  }
}

function* deleteLookupDataSaga(
  action: ReturnType<typeof deleteLookupDataSagaAction>
) {
  try {
    yield put(lookupDataSliceAction.setLookupDeleteLoading(true));
    const response: DeleteLookupDataApiResponseType = yield call(
      deleteLookupDataAPI,
      action.payload.ID
    );
    action.payload.onSuccess?.(response?.message || "Data deleted successfully.");
  } catch (error: any) {
    const message =
      error?.response?.data?.message || "Unable to delete lookup data.";
    yield put(lookupDataSliceAction.setLookupErrorMessage(message));
    action.payload.onError?.(message);
  } finally {
    yield put(lookupDataSliceAction.setLookupDeleteLoading(false));
  }
}

function* lookupMappingsSaga(
  action: ReturnType<typeof lookupMappingsSagaAction>
) {
  try {
    yield put(lookupDataSliceAction.setLookupMappingsLoading(true));
    const response: LookupMappingsApiResponseType = yield call(lookupMappingsAPI, {
      category: action.payload.category,
      module: action.payload.module,
      ids: action.payload.ids,
    });
    action.payload.onSuccess?.(
      response?.message || "Lookup mappings updated successfully."
    );
  } catch (error: any) {
    const message =
      error?.response?.data?.message || "Unable to update lookup mappings.";
    yield put(lookupDataSliceAction.setLookupErrorMessage(message));
    action.payload.onError?.(message);
  } finally {
    yield put(lookupDataSliceAction.setLookupMappingsLoading(false));
  }
}

function* lookupDataSaga() {
  yield takeEvery(
    lookupDataSagaActionTypes.callGetLookupByCategorySaga,
    getLookupByCategorySaga
  );
  yield takeLatest(
    lookupDataSagaActionTypes.callCreateLookupDataSaga,
    createLookupDataSaga
  );
  yield takeLatest(
    lookupDataSagaActionTypes.callDeleteLookupDataSaga,
    deleteLookupDataSaga
  );
  yield takeLatest(
    lookupDataSagaActionTypes.callLookupMappingsSaga,
    lookupMappingsSaga
  );
}

export default lookupDataSaga;
